
import { DiaryEntry } from '../types';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { entryToSupabaseFormat, supabaseToEntryFormat } from '../transformers';
import { handleError } from '../errorHandling';
import { findLocalEntryById, deleteLocalEntry, updateLocalEntry } from './localStorage';

/**
 * Get a single diary entry by ID
 */
export async function getEntry(id: string): Promise<DiaryEntry | undefined> {
  try {
    const { data, error } = await supabase
      .from('diary_entries')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) throw error;
    
    if (data) {
      return supabaseToEntryFormat(data);
    }
    
    return findLocalEntryById(id);
  } catch (error) {
    handleError(error, "Error fetching diary entry");
    return findLocalEntryById(id);
  }
}

/**
 * Delete a diary entry
 */
export async function deleteEntry(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('diary_entries')
      .delete()
      .eq('id', id);
      
    if (error) throw error;
    
    deleteLocalEntry(id);
  } catch (error) {
    handleError(error, "Failed to delete diary entry");
  }
}

/**
 * Update an existing diary entry
 */
export async function updateEntry(updatedEntry: DiaryEntry): Promise<void> {
  if (!updatedEntry.id || !updatedEntry.userId) {
    console.error('Attempted to update entry without id or userId');
    return;
  }
  
  try {
    const supabaseEntry = entryToSupabaseFormat(updatedEntry);
    
    // Add the metadata fields to the update
    const entryWithMetadata = {
      ...supabaseEntry,
      height: updatedEntry.height,
      weight: updatedEntry.weight,
      diaper_changes: updatedEntry.diaperChanges,
      milk_intake: updatedEntry.milkIntake,
      activities: updatedEntry.activities ? JSON.stringify(updatedEntry.activities) : null,
      milestones: updatedEntry.milestones ? JSON.stringify(updatedEntry.milestones) : null,
      images: updatedEntry.images ? JSON.stringify(updatedEntry.images) : null,
    };
    
    const { error } = await supabase
      .from('diary_entries')
      .update(entryWithMetadata)
      .eq('id', updatedEntry.id);
      
    if (error) throw error;
    
    updateLocalEntry(updatedEntry);
    toast.success("Entry updated successfully");
  } catch (error) {
    handleError(error, "Failed to update diary entry");
    updateLocalEntry(updatedEntry);
  }
}

/**
 * Upload an image for a diary entry
 */
export async function uploadEntryImage(file: File, userId: string): Promise<string | null> {
  if (!file) {
    console.error('No file provided');
    return null;
  }

  if (!userId) {
    console.error('No userId provided');
    return null;
  }

  console.log('Starting upload process for file:', file.name, 'type:', file.type, 'size:', file.size);
  
  try {
    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      toast.error("Only image files are allowed");
      return null;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB max
      toast.error("Image size should be less than 5MB");
      return null;
    }
    
    // Sanitize filename and create path
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const sanitizedUserId = userId.replace(/[^a-zA-Z0-9]/g, '');
    const fileName = `${sanitizedUserId}_${Date.now()}.${fileExt}`;
    const filePath = `diary_images/${fileName}`;
    
    console.log('Uploading to path:', filePath);
    
    // Upload the file to Supabase Storage with the user as the owner
    const { error: uploadError, data: uploadData } = await supabase
      .storage
      .from('baby_diary')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      });
      
    if (uploadError) {
      console.error('Upload error:', uploadError);
      
      if (uploadError.message.includes('The resource already exists')) {
        toast.error("A file with this name already exists. Please try again.");
      } else if (uploadError.message.includes('row-level security policy')) {
        toast.error("Permission denied. You don't have access to upload files.");
        console.error("RLS policy error, ensure the policy allows this user to upload:", userId);
      } else {
        toast.error(`Upload error: ${uploadError.message}`);
      }
      return null;
    }
    
    console.log('Upload successful, data:', uploadData);
    
    // Get the public URL for the uploaded file
    const { data: urlData } = supabase
      .storage
      .from('baby_diary')
      .getPublicUrl(filePath);
    
    if (!urlData || !urlData.publicUrl) {
      console.error('Failed to get public URL');
      toast.error("Failed to get image URL after upload");
      return null;
    }
    
    console.log('Got public URL:', urlData.publicUrl);
    
    return urlData.publicUrl;
  } catch (error) {
    console.error('Error in uploadEntryImage:', error);
    handleError(error, "Failed to upload image");
    return null;
  }
}
